// CreateTender — call a tender from a type's template.
//
// Connects to: GET /api/townforge/tender-types (the catalog) and POST /api/townforge/tenders/{orgId}.
// The server copies the chosen type's requirement and criterion templates into the new tender
// (TD-008) — so picking a type here is not a label, it is what the tenderer will have to answer and
// what they will be scored on.
//
// Two things this form is careful about:
//
//   1. A TYPE WITH NO TEMPLATE IS SHOWN AS UNUSABLE, not hidden and not silently allowed. Calling
//      one produces a tender with no declared submission format — nothing to answer, nothing for
//      forgeBid to check. Hiding it would leave an officer wondering where the type went; allowing
//      it would let them discover the problem after bids arrive.
//   2. THE CONTRACT FIELDS APPEAR ONLY FOR A TERM CONTRACT. A maintenance regime is administered
//      by date and defined by the equipment it covers; a works tender is not, and showing empty
//      equipment rows on one would teach the wrong thing about what is being bought.
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { api, ApiError, type TenderType } from '../lib/api'

interface EquipRow { cls: string; location: string; count: string }

export default function CreateTender() {
  const { me } = useAuth()
  const orgId = me?.org?.id ?? null
  const nav = useNavigate()

  const [types, setTypes] = useState<TenderType[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [typeKey, setTypeKey] = useState('')
  const [tenderRef, setTenderRef] = useState('')
  const [title, setTitle] = useState('')
  const [division, setDivision] = useState('')
  const [location, setLocation] = useState('')
  const [periodMonths, setPeriodMonths] = useState('')
  const [estimatedValue, setEstimatedValue] = useState('')
  const [closesAt, setClosesAt] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [equip, setEquip] = useState<EquipRow[]>([{ cls: '', location: '', count: '' }])

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    api.tenderTypes()
      .then(r => { if (!cancelled) setTypes(r.types) })
      .catch((e: unknown) => {
        if (cancelled) return
        setLoadError(e instanceof ApiError
          ? `Could not load the tender types: ${e.detail}`
          : 'Could not reach TownForge.')
      })
    return () => { cancelled = true }
  }, [])

  const chosen = types?.find(t => t.key === typeKey) ?? null
  // A term contract is administered by date and defined by what it covers. Anything else is not.
  const isTerm = chosen?.category === 'term_service'

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!orgId || !chosen) return

    // Checked here for a usable message, and enforced by a CHECK constraint in the database
    // (0005) so no path can create such a contract even if this form is bypassed.
    if (startDate && endDate && endDate <= startDate) {
      setError('The contract end date must be after the start date.')
      return
    }

    setSaving(true); setError(null)
    try {
      const body: Record<string, unknown> = {
        tenderRef: tenderRef.trim(),
        title: title.trim(),
        tenderTypeKey: typeKey,
        division: division.trim() || null,
        location: location.trim() || null,
        contractPeriodMonths: periodMonths ? Number(periodMonths) : null,
        estimatedValue: estimatedValue ? Number(estimatedValue) : null,
        // datetime-local has no offset. Sent as typed and resolved server-side rather than
        // stamped with the browser's zone here — the close time decides admissibility (TD-010)
        // and must not depend on the caller's clock settings.
        closesAt: closesAt || null,
      }
      if (isTerm) {
        body.contractStartDate = startDate || null
        body.contractEndDate = endDate || null
        body.equipment = equip
          .filter(r => r.cls.trim())
          .map(r => ({
            class: r.cls.trim(),
            location: r.location.trim() || null,
            count: r.count ? Number(r.count) : null,
          }))
      }
      const created = await api.tenders.create(orgId, body)
      nav(`/tc/tenders/${created.id}`)
    } catch (e: unknown) {
      // The server's message is shown verbatim: it names the field or the rule that refused, which
      // is more use than anything this form could infer.
      setError(e instanceof ApiError ? e.detail : 'Could not reach TownForge.')
      setSaving(false)
    }
  }

  if (!orgId) return <div className="page"><p className="page-state">
    Your account is not a member of a Town Council.</p></div>
  if (loadError) return <div className="page">
    <p className="page-state page-state-error">{loadError}</p></div>
  if (!types) return <div className="page"><p className="page-state">Loading…</p></div>

  return (
    <div className="page">
      <header className="page-head">
        <h1>Call a tender</h1>
        <p className="page-sub">
          The type you choose brings its requirement set and evaluation matrix with it. The tender
          is created as a draft — nothing is published until you publish it.
        </p>
      </header>

      <form onSubmit={submit} className="form">
        <label className="field">
          <span className="field-lbl">Tender type</span>
          <select value={typeKey} onChange={e => setTypeKey(e.target.value)} required>
            <option value="">Choose a type…</option>
            {types.map(t => (
              <option key={t.key} value={t.key} disabled={!t.hasTemplate}>
                {t.name}{t.hasTemplate
                  ? ` — ${t.requirementCount} requirements, ${t.criterionCount} criteria`
                  : ' — no template yet'}
              </option>
            ))}
          </select>
          {chosen && (
            <span className="field-note">
              {chosen.description} Scoring: {chosen.evalModel.toUpperCase()}, {chosen.priceDirection} price wins.
            </span>
          )}
          {!chosen && (
            <span className="field-note">
              A type shown as “no template yet” cannot be called: it would produce a tender with no
              declared submission format, and nothing for a tenderer to answer.
            </span>
          )}
        </label>

        <label className="field">
          <span className="field-lbl">Tender reference</span>
          <input value={tenderRef} onChange={e => setTenderRef(e.target.value)}
                 placeholder="ZZ/MNE/2026/003" required />
          <span className="field-note">Must be unique within this Town Council.</span>
        </label>

        <label className="field">
          <span className="field-lbl">Title</span>
          <input value={title} onChange={e => setTitle(e.target.value)}
                 placeholder="Term Contract for M&amp;E Maintenance, Zenith Estate" required />
        </label>

        <div className="field-row">
          <label className="field">
            <span className="field-lbl">Division</span>
            <input value={division} onChange={e => setDivision(e.target.value)} placeholder="Estates" />
          </label>
          <label className="field">
            <span className="field-lbl">Place</span>
            <input value={location} onChange={e => setLocation(e.target.value)}
                   placeholder="Zenith Estate" />
          </label>
        </div>

        <div className="field-row">
          <label className="field">
            <span className="field-lbl">Estimated value (SGD)</span>
            <input type="number" min="0" step="0.01" value={estimatedValue}
                   onChange={e => setEstimatedValue(e.target.value)} placeholder="1450000" />
          </label>
          <label className="field">
            <span className="field-lbl">Contract period (months)</span>
            <input type="number" min="1" value={periodMonths}
                   onChange={e => setPeriodMonths(e.target.value)} placeholder="36" />
          </label>
        </div>

        <label className="field">
          <span className="field-lbl">Tender closes</span>
          <input type="datetime-local" value={closesAt} onChange={e => setClosesAt(e.target.value)} />
          <span className="field-note">
            The moment the box seals. One second decides whether a bid is admissible, so this is
            held and enforced on the server clock, never a browser’s.
          </span>
        </label>

        {isTerm && (
          <fieldset className="fieldset">
            <legend>Contract and scope</legend>
            <p className="field-note">
              A term contract is administered by date and defined by what it covers. The period
              above is what is advertised; these dates are what is administered, and neither is
              derived from the other.
            </p>

            <div className="field-row">
              <label className="field">
                <span className="field-lbl">Contract starts</span>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
              </label>
              <label className="field">
                <span className="field-lbl">Contract ends</span>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
              </label>
            </div>

            <span className="field-lbl">Equipment to be maintained</span>
            {equip.map((row, i) => (
              <div className="field-row" key={i}>
                <label className="field">
                  <input value={row.cls} placeholder="Passenger lift"
                         onChange={e => setEquip(equip.map((r, j) =>
                           j === i ? { ...r, cls: e.target.value } : r))} />
                </label>
                <label className="field">
                  <input value={row.location} placeholder="Blk 101–105"
                         onChange={e => setEquip(equip.map((r, j) =>
                           j === i ? { ...r, location: e.target.value } : r))} />
                </label>
                <label className="field field-narrow">
                  <input type="number" min="1" value={row.count} placeholder="Qty"
                         onChange={e => setEquip(equip.map((r, j) =>
                           j === i ? { ...r, count: e.target.value } : r))} />
                </label>
              </div>
            ))}
            <button type="button" className="btn-quiet"
                    onClick={() => setEquip([...equip, { cls: '', location: '', count: '' }])}>
              Add equipment
            </button>
            <p className="field-note">
              Certifications and constraints are not entered here — they come from the type’s
              requirement template, so they are things a tenderer must evidence and the advisor can
              check, rather than free text nobody can act on.
            </p>
          </fieldset>
        )}

        {error && <p className="page-state page-state-error">{error}</p>}

        <div className="form-actions">
          <button type="submit" disabled={saving || !chosen?.hasTemplate}>
            {saving ? 'Creating…' : 'Create draft tender'}
          </button>
          <span className="field-note">
            Creates a draft. Publishing is a separate, deliberate step.
          </span>
        </div>
      </form>
    </div>
  )
}
